import { useEffect, useId, useRef } from 'react';
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore';

/**
 * useUnsavedGuard
 *
 * Registers an editor instance's dirty state with the shared unsaved-changes
 * registry (`useUnsavedChangesStore`). While any registered instance is
 * dirty, the auto-reload mechanism (see `index.html`) will skip a silent
 * background reload and fall back to showing the manual "Refresh" banner
 * instead, so in-progress edits are never lost.
 *
 * Phase 1: mechanism only. Phase 2 wires this into actual editors
 * (PostCreator, etc.).
 *
 * @param isDirty - whether the calling editor currently has unsaved changes
 * @param id - optional stable id prefix (useful for debugging); a unique
 *             suffix is always appended so multiple instances never collide
 */
export function useUnsavedGuard(isDirty: boolean, id?: string): void {
  const generatedId = useId();
  const instanceIdRef = useRef<string>(id ? `${id}-${generatedId}` : generatedId);
  const setDirty = useUnsavedChangesStore((state) => state.setDirty);
  const clear = useUnsavedChangesStore((state) => state.clear);

  useEffect(() => {
    setDirty(instanceIdRef.current, isDirty);
  }, [isDirty, setDirty]);

  // Unregister on unmount so a closed editor never leaves a stuck-dirty entry.
  useEffect(() => {
    const instanceId = instanceIdRef.current;
    return () => {
      clear(instanceId);
    };
  }, [clear]);
}
